import * as React from 'react';
import { type MouseEvent, useEffect, useState } from 'react';
import { setShowAuth, useAuthStore } from '@/stores/auth.store.ts';
import { useLoginLogout } from '@/hooks/use-login-logout.ts';
import { LogOutIcon, SettingsIcon } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { config } from '@/config';

export type MenuItem = {
  text: string;
  href: string;
  search?: Record<string, any>;
  sub?: MenuItem[];
  auth?: boolean;
  icon?: React.ReactNode;
};

export const menuItems: MenuItem[] = [
  {
    text: 'SHOP',
    href: '/products',
    sub: [
      { text: 'ALL', href: '/products', auth: true },
      { text: 'BROADLOOM', href: '/products', auth: true, search: {type: 1} },
      { text: 'BESPOKE', href: '/products', auth: true, search: {type: 0} },
      { text: 'RUGS', href: '/rugs', auth: true, search: {} },
    ],
  },
  {
    text: 'INSTALLATIONS',
    href: '#',
    sub: [
      { text: 'RESIDENTIAL', href: '/installations-residential' },
      { text: 'HOSPITALITY', href: '/installations-hospitality' },
    ],
  },
  {
    text: 'ABOUT US',
    href: '/about',
  },
];

export function useHeaderNavigation() {
  const user = useAuthStore((state) => state.user);
  const { doLogout } = useLoginLogout();
  const [userMenuItems, setUserMenuItems] = useState<MenuItem[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const items: MenuItem[] = [];
      items.push({ text: 'Samples', href: '/account/samples' });
      items.push({ text: 'Projects', href: '/account/projects' });
      // items.push({ text: 'Meetings', href: '/account/meetings' });
      items.push({ text: 'Account', href: '/account/profile' });
      if (user.admin) {
        items.push({ text: '---', href: '#a1' });
        items.push({
          text: 'Admin',
          href: config.getAdminUrl('/admin/dashboard'),
          icon: <SettingsIcon size={15} />,
        });
      }
      items.push({ text: '---', href: '#u1' });
      items.push({
        text: 'Logout',
        href: '/logout',
        icon: <LogOutIcon size={15} className="w-4" />,
      });
      setUserMenuItems(items);
    }
  }, [user]);

  const onMenuClick = async (e: MouseEvent<HTMLElement>, item: MenuItem) => {
    e.preventDefault();
    if (item.href == '/logout') {
      doLogout();
      return false;
    }
    if (item.auth && !user) {
      setShowAuth(true, item.href);
      return false;
    }
    if (item.href.startsWith('http')) {
      window.open(item.href, '_blank');
      return false;
    }
    await navigate({ to: item.href, search: item.search, viewTransition: true });
  };

  const toggleSub = (sub: HTMLElement, show: boolean | null = null) => {
    if (sub) {
      if (show === null) {
        show = sub.classList.contains('hidden');
      }
      if (show) {
        sub.classList.remove('hidden', 'opacity-100');
        sub.classList.add('opacity-0');
        setTimeout(() => {
          sub.classList.add('opacity-100');
        }, 1);
      } else {
        sub.classList.remove('opacity-100');
        setTimeout(() => {
          if (!sub.classList.contains('opacity-100')) {
            sub.classList.add('hidden');
          }
        }, 300);
      }
    }
  };

  const hoverOn = (e: MouseEvent<HTMLElement>) => {
    const btn = e.target as HTMLElement;
    const sub = btn.nextElementSibling as HTMLElement;
    toggleSub(sub, true);
  };

  const hoverOff = (e: MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    let sub = target.nextElementSibling as HTMLElement;
    if (!sub) {
      sub = target.parentElement?.nextElementSibling as HTMLElement;
    }
    if (!sub || !sub.classList.contains('sub')) {
      sub = target.parentElement as HTMLElement;
    }
    toggleSub(sub, false);
  };

  const onSubClick = (e: MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    let sub = target.nextElementSibling as HTMLElement;
    if (!sub) {
      sub = target.parentElement?.nextElementSibling as HTMLElement;
    }
    if (sub) {
      toggleSub(sub);
    }
  };

  return { onMenuClick, user, userMenuItems, hoverOn, hoverOff, onSubClick };
}
